import * as THREE from "three";
import type { QualityProfile } from "../app/performance";
import {
  DiceDuel,
  type DiceRollResult,
} from "../games/dice/DiceDuel";
import {
  CreatureHuntArena,
  type HuntShotResult,
} from "../games/hunt/CreatureHuntArena";

export type WorldMode = "lobby" | "hunt" | "dice";

export class GameWorld {
  private readonly renderer: THREE.WebGLRenderer;
  private readonly scene = new THREE.Scene();
  private readonly camera = new THREE.PerspectiveCamera(46, 1, 0.1, 80);
  private readonly clock = new THREE.Clock();
  private readonly lobby = new THREE.Group();
  private readonly particles: THREE.Points;
  private readonly arena: CreatureHuntArena;
  private readonly dice: DiceDuel;
  private readonly huntReady: Promise<void>;
  private readonly basePixelRatio: number;
  private readonly dicePixelRatio: number;
  private readonly baseExposure: number;
  private diceCameraZ = 4.35;
  private diceCameraY = 2.38;

  private mode: WorldMode = "lobby";
  private frameId = 0;
  private disposed = false;
  private cameraKick = 0;
  private aimX = 0;
  private aimY = 0;
  private drawPower = 0;
  private scoped = false;

  constructor(
    private readonly host: HTMLElement,
    quality: QualityProfile,
    onShot: (result: HuntShotResult) => void,
    onDiceRoll: (result: DiceRollResult) => void,
  ) {
    this.basePixelRatio = quality.pixelRatio;
    this.dicePixelRatio = Math.min(
      window.devicePixelRatio || 1,
      quality.tier === "performance" ? 1.65 : 1.9,
    );
    this.baseExposure = quality.tier === "performance" ? 1.0 : 1.08;

    this.renderer = new THREE.WebGLRenderer({
      antialias: quality.antialias,
      alpha: false,
      powerPreference: quality.tier === "performance" ? "low-power" : "high-performance",
    });
    this.renderer.setPixelRatio(this.basePixelRatio);
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = this.baseExposure;
    this.renderer.shadowMap.enabled = quality.shadows;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    this.host.appendChild(this.renderer.domElement);

    this.scene.background = new THREE.Color(0x0b1017);
    this.scene.fog = new THREE.FogExp2(0x0b1017, 0.05);
    this.camera.position.set(0, 1.55, 5.4);
    this.camera.lookAt(0, 1.12, 0);

    this.scene.add(this.buildLights(quality), this.lobby);
    this.buildLobby(quality);

    this.particles = this.buildParticles(quality.particles);
    this.scene.add(this.particles);

    this.arena = new CreatureHuntArena(quality, (result) => {
      this.cameraKick = result.hit
        ? result.label === "CRITICAL"
          ? 0.095
          : 0.055
        : 0.02;
      onShot(result);
    });
    this.scene.add(this.arena.group);

    this.dice = new DiceDuel(quality, (result) => {
      this.cameraKick = result.outcome === "win" ? 0.065 : 0.025;
      onDiceRoll(result);
    });
    this.scene.add(this.dice.group);

    // Hunt assets continue to preload in the background. Dice never depends on
    // them, so entering Dice Duel stays immediate even on slow networks.
    this.huntReady = this.arena.prepare();

    this.resize();
    window.addEventListener("resize", this.resize);
    this.frame();
  }

  async prepareHunt(): Promise<boolean> {
    await this.huntReady;
    return this.arena.isProfessionalAssetReady();
  }

  setMode(mode: WorldMode): void {
    if (mode === this.mode) return;

    this.mode = mode;
    this.lobby.visible = mode === "lobby";
    this.cameraKick = 0;
    this.aimX = 0;
    this.aimY = 0;
    this.drawPower = 0;
    this.scoped = false;

    if (mode === "hunt") {
      this.setRenderPixelRatio(this.basePixelRatio);
      this.renderer.toneMappingExposure = this.baseExposure;
      this.dice.setActive(false);
      this.arena.setActive(true);

      this.scene.background = new THREE.Color(0x9db2a0);
      this.scene.fog = new THREE.Fog(0x9db2a0, 10, 34);

      this.camera.fov = 54;
      this.camera.updateProjectionMatrix();
      this.camera.position.set(0, 1.58, 3.8);
      this.camera.lookAt(0, 1.18, -7.0);
      return;
    }

    this.arena.setActive(false);
    this.arena.clear();

    if (mode === "dice") {
      // Dice is a lightweight scene, so render it sharper than the heavy Hunt
      // without making the forest scene slower on the same phone.
      this.setRenderPixelRatio(this.dicePixelRatio);
      this.renderer.toneMappingExposure = 1.16;
      this.dice.setActive(true);

      this.scene.background = new THREE.Color(0x080b14);
      this.scene.fog = new THREE.FogExp2(0x080b14, 0.024);

      this.camera.fov = 37;
      this.camera.updateProjectionMatrix();
      this.fitDiceCamera();
      return;
    }

    this.setRenderPixelRatio(this.basePixelRatio);
    this.renderer.toneMappingExposure = this.baseExposure;
    this.dice.setActive(false);

    this.scene.background = new THREE.Color(0x0b1017);
    this.scene.fog = new THREE.FogExp2(0x0b1017, 0.05);

    this.camera.fov = 46;
    this.camera.updateProjectionMatrix();
    this.camera.position.set(0, 1.55, 5.4);
    this.camera.lookAt(0, 1.12, 0);
  }

  resetRound(): void {
    if (this.mode !== "hunt") return;

    this.aimX = 0;
    this.aimY = 0;
    this.drawPower = 0;
    this.arena.reset();
    this.arena.setAim(0, 0);
    this.arena.setDrawPower(0);
  }

  resetDice(): void {
    if (this.mode !== "dice") return;
    this.dice.reset();
  }

  rollDice(): boolean {
    if (this.mode !== "dice") return false;
    return this.dice.roll();
  }

  isDiceRolling(): boolean {
    return this.mode === "dice" && this.dice.isRolling();
  }

  aim(x: number, y: number): void {
    this.aimX = THREE.MathUtils.clamp(x, -1, 1);
    this.aimY = THREE.MathUtils.clamp(y, -1, 1);
    this.arena.setAim(this.aimX, this.aimY);
  }

  setScoped(value: boolean): void {
    this.scoped = value;
    this.arena.setScoped(value);
  }

  setDrawPower(power: number): void {
    this.drawPower = THREE.MathUtils.clamp(power, 0, 1);
    this.arena.setDrawPower(this.drawPower);
  }

  shoot(power: number): void {
    if (this.mode !== "hunt") return;

    this.cameraKick = Math.max(this.cameraKick, 0.04);
    this.arena.shoot(power);
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;

    cancelAnimationFrame(this.frameId);
    window.removeEventListener("resize", this.resize);
    this.arena.clear();

    this.scene.traverse((object: THREE.Object3D) => {
      if (!(object instanceof THREE.Mesh) && !(object instanceof THREE.Points)) return;

      object.geometry.dispose();
      const material = object.material;
      const materials = Array.isArray(material) ? material : [material];
      for (const entry of materials) entry.dispose();
    });

    this.renderer.dispose();
    this.renderer.domElement.remove();
  }

  private fitDiceCamera(): void {
    // Frame both dice on narrow portrait screens without changing their meshes
    // or reducing Dice mode's sharper pixel ratio.
    const halfFov = Math.tan(THREE.MathUtils.degToRad(this.camera.fov * 0.5));
    const framingWidth = 3.6;
    this.diceCameraZ = Math.max(
      4.35,
      framingWidth / (2 * halfFov * Math.max(this.camera.aspect, 0.1)),
    );
    // Preserve the original top/side view as the camera moves back.
    this.diceCameraY = 0.43 + (this.diceCameraZ - 0.02) * (1.95 / 4.33);
    this.camera.position.set(0, this.diceCameraY, this.diceCameraZ);
    this.camera.lookAt(0, 0.43, 0.02);
  }

  private setRenderPixelRatio(pixelRatio: number): void {
    this.renderer.setPixelRatio(pixelRatio);
    this.resize();
  }

  private readonly resize = (): void => {
    const width = Math.max(1, this.host.clientWidth);
    const height = Math.max(1, this.host.clientHeight);

    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(width, height, false);
    if (this.mode === "dice") this.fitDiceCamera();
  };

  private readonly frame = (): void => {
    if (this.disposed) return;

    const delta = Math.min(this.clock.getDelta(), 0.05);
    const elapsed = this.clock.elapsedTime;

    this.cameraKick = THREE.MathUtils.damp(this.cameraKick, 0, 14, delta);

    if (this.mode === "lobby") {
      this.lobby.rotation.y = Math.sin(elapsed * 0.24) * 0.05;
      this.lobby.position.y = Math.sin(elapsed * 0.5) * 0.012;

      this.camera.position.x = Math.sin(elapsed * 0.18) * 0.08;
      this.camera.position.y = 1.55 + Math.sin(elapsed * 0.22) * 0.02;
      this.camera.position.z = 5.4;
      this.camera.lookAt(0, 1.12, 0);
    } else if (this.mode === "hunt") {
      this.arena.update(delta, elapsed);

      const scopeFactor = this.scoped ? 0.45 : 1;
      const runFactor = scopeFactor * (1 - this.drawPower * 0.62);
      const cadence = elapsed * 9.1;
      const headBobX = Math.sin(cadence * 0.5) * 0.024 * runFactor;
      const headBobY = Math.abs(Math.sin(cadence)) * 0.036 * runFactor;

      this.camera.position.x = THREE.MathUtils.damp(
        this.camera.position.x,
        this.aimX * 0.085 + headBobX,
        12,
        delta,
      );
      this.camera.position.y = THREE.MathUtils.damp(
        this.camera.position.y,
        1.58 - headBobY + this.aimY * 0.035,
        12,
        delta,
      );
      this.camera.position.z = 3.8 + this.cameraKick;

      const targetX = this.aimX * 3.4;
      const targetY = 1.24 + this.aimY * 2.0;
      this.camera.lookAt(targetX, targetY, -7.0);

      const targetFov =
        (this.scoped ? 43.5 : 54) -
        this.drawPower * (this.scoped ? 2.4 : 4.6);
      const nextFov = THREE.MathUtils.damp(this.camera.fov, targetFov, 7.5, delta);
      if (Math.abs(nextFov - this.camera.fov) > 0.001) {
        this.camera.fov = nextFov;
        this.camera.updateProjectionMatrix();
      }
    } else {
      this.dice.update(delta, elapsed);

      const targetY = this.diceCameraY + Math.sin(elapsed * 0.48) * 0.012;
      this.camera.position.x = THREE.MathUtils.damp(
        this.camera.position.x,
        Math.sin(elapsed * 0.3) * 0.035,
        6,
        delta,
      );
      this.camera.position.y = THREE.MathUtils.damp(
        this.camera.position.y,
        targetY,
        6,
        delta,
      );
      this.camera.position.z = this.diceCameraZ + this.cameraKick;
      this.camera.lookAt(0, 0.43, 0.02);
    }

    this.particles.visible = this.mode === "lobby";
    if (this.particles.visible) {
      this.particles.rotation.y += delta * 0.016;
      this.particles.rotation.x = Math.sin(elapsed * 0.1) * 0.015;
    }

    this.renderer.render(this.scene, this.camera);
    this.frameId = requestAnimationFrame(this.frame);
  };

  private buildLights(quality: QualityProfile): THREE.Group {
    const lights = new THREE.Group();

    const sky = new THREE.HemisphereLight(0xdce9ff, 0x536049, 1.6);
    lights.add(sky);

    const sun = new THREE.DirectionalLight(0xfff2d4, 2.35);
    sun.position.set(-4.5, 8.5, 5.5);
    sun.castShadow = quality.shadows;

    if (quality.shadows) {
      const size = quality.tier === "quality" ? 1024 : 512;
      sun.shadow.mapSize.set(size, size);
      sun.shadow.camera.near = 0.5;
      sun.shadow.camera.far = 32;
      sun.shadow.camera.left = -8;
      sun.shadow.camera.right = 8;
      sun.shadow.camera.top = 8;
      sun.shadow.camera.bottom = -8;
    }

    lights.add(sun);

    const fill = new THREE.DirectionalLight(0xb8d2c2, 0.58);
    fill.position.set(5, 3, -4);
    lights.add(fill);

    return lights;
  }

  private buildLobby(quality: QualityProfile): void {
    const floor = new THREE.Mesh(
      new THREE.CylinderGeometry(2.7, 3.05, 0.22, 64),
      new THREE.MeshStandardMaterial({
        color: 0x111923,
        metalness: 0.38,
        roughness: 0.5,
      }),
    );
    floor.position.y = -0.18;
    floor.receiveShadow = quality.shadows;
    this.lobby.add(floor);

    const pedestal = new THREE.Mesh(
      new THREE.CylinderGeometry(0.92, 1.12, 0.36, 48),
      new THREE.MeshStandardMaterial({
        color: 0x172131,
        roughness: 0.36,
        metalness: 0.52,
      }),
    );
    pedestal.position.y = 0.04;
    pedestal.castShadow = quality.shadows;
    this.lobby.add(pedestal);

    const bowSilhouette = new THREE.Group();
    const material = new THREE.MeshStandardMaterial({
      color: 0x755033,
      roughness: 0.42,
      metalness: 0.04,
    });

    const left = new THREE.Mesh(
      new THREE.TorusGeometry(0.68, 0.035, 10, 48, Math.PI * 0.86),
      material,
    );
    left.rotation.z = Math.PI * 0.57;
    bowSilhouette.add(left);

    const string = new THREE.Line(
      new THREE.BufferGeometry().setFromPoints([
        new THREE.Vector3(-0.53, 0.56, 0),
        new THREE.Vector3(0.1, 0, 0),
        new THREE.Vector3(-0.53, -0.56, 0),
      ]),
      new THREE.LineBasicMaterial({ color: 0xd9d5cb, transparent: true, opacity: 0.75 }),
    );
    bowSilhouette.add(string);

    bowSilhouette.position.set(0, 1.18, 0);
    bowSilhouette.rotation.z = -0.08;
    bowSilhouette.scale.setScalar(1.12);
    this.lobby.add(bowSilhouette);
  }

  private buildParticles(count: number): THREE.Points {
    const geometry = new THREE.BufferGeometry();
    const points = new Float32Array(count * 3);

    for (let i = 0; i < count; i += 1) {
      const offset = i * 3;
      points[offset] = (Math.random() - 0.5) * 10;
      points[offset + 1] = Math.random() * 5.5;
      points[offset + 2] = (Math.random() - 0.5) * 9;
    }

    geometry.setAttribute("position", new THREE.BufferAttribute(points, 3));

    return new THREE.Points(
      geometry,
      new THREE.PointsMaterial({
        color: 0xaebaff,
        size: 0.024,
        transparent: true,
        opacity: 0.42,
        depthWrite: false,
      }),
    );
  }
}
